// @flow
import React, { PureComponent } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Text } from 'trpc-react-native-ui';
import { FullScreenModal } from '../../../common';
import PageTitle from '../../../signUp/PageTitle';
import I18n from '../../../../lib/i18n';
import GradientPage from '../../../common/GradientPage';
import { OptionSlider } from '../../../common/CharacteristicSlider';
import { isEqual } from 'lodash';

import theme from '../../../../theme-config';
import { formatValue } from '../../../../lib/usage-formatters';

const options = [
  { characteristic: { value: 'RARELY' } },
  { characteristic: { value: 'SOMETIMES' } },
  { characteristic: { value: 'OFTEN' } },
  { characteristic: { value: 'VERY_OFTEN' } },
  { characteristic: { value: 'ADDICT' } },
];

const categories = [
  {
    name: 'VIDEO',
    options: [
      { ...options[0], data: 0.6, category: 'VIDEO' },
      { ...options[1], data: 2.4, category: 'VIDEO' },
      { ...options[2], data: 6, category: 'VIDEO' },
      { ...options[3], data: 9.6, category: 'VIDEO' },
      { ...options[4], data: 18, category: 'VIDEO' },
    ],
  },
  {
    name: 'MUSIC_GAMES',
    options: [
      { ...options[0], data: 0.15, category: 'MUSIC_GAMES' },
      { ...options[1], data: 0.3, category: 'MUSIC_GAMES' },
      { ...options[2], data: 0.5, category: 'MUSIC_GAMES' },
      { ...options[3], data: 1.5, category: 'MUSIC_GAMES' },
      { ...options[4], data: 2.5, category: 'MUSIC_GAMES' },
    ],
  },
  {
    name: 'SOCIAL_MEDIA',
    options: [
      { ...options[0], data: 0.5, category: 'SOCIAL_MEDIA' },
      { ...options[1], data: 2.5, category: 'SOCIAL_MEDIA' },
      { ...options[2], data: 5, category: 'SOCIAL_MEDIA' },
      { ...options[3], data: 10, category: 'SOCIAL_MEDIA' },
      { ...options[4], data: 15, category: 'SOCIAL_MEDIA' },
    ],
  },
  {
    name: 'WEB_BROWSING',
    options: [
      { ...options[0], data: 0.3, category: 'WEB_BROWSING' },
      { ...options[1], data: 0.6, category: 'WEB_BROWSING' },
      { ...options[2], data: 1.2, category: 'WEB_BROWSING' },
      { ...options[3], data: 1.8, category: 'WEB_BROWSING' },
      { ...options[4], data: 3, category: 'WEB_BROWSING' },
    ],
  },
  {
    name: 'EMAIL',
    options: [
      { ...options[0], data: 0.1, category: 'EMAIL' },
      { ...options[1], data: 0.25, category: 'EMAIL' },
      { ...options[2], data: 0.5, category: 'EMAIL' },
      { ...options[3], data: 1.2, category: 'EMAIL' },
      { ...options[4], data: 2, category: 'EMAIL' },
    ],
  },
];

export default class DataHelpModal extends PureComponent<Props, State> {
  styles = getStyles();

  state = {
    selectedOptions: { options: categories.map(category => category.options[0]) },
  };

  addOption = (newOption: Object, optionToRemove: Object) => {
    if (optionToRemove) {
      this.setState(({ selectedOptions }) => ({
        selectedOptions: selectedOptions.options.filter(option => !isEqual(option, optionToRemove)),
      }));
    }
    if (newOption) {
      this.setState(({ selectedOptions }) => ({
        selectedOptions: { ...selectedOptions, options: [...selectedOptions.options, newOption] },
      }));
    }
  };

  computeUsage = () => {
    let usage = 0;
    this.state.selectedOptions.options.forEach(option => {
      usage += option.data;
    });
    return usage * 1024 * 1024; // convert GB to KB (pre-1998)
  };

  optionKeyExtractor = (option: Object) => option?.characteristic?.value;

  render() {
    const styles = this.styles;

    return (
      <FullScreenModal {...this.props}>
        <GradientPage>
          <View style={styles.container}>
            <PageTitle title={I18n.t('SignUp.DataHelpModal.title')} style={styles.title} />
            <ScrollView contentContainerStyle={styles.scrollViewContent}>
              {categories.map(category => (
                <OptionSlider
                  key={category.name}
                  name={category.name}
                  options={category.options}
                  optionKeyExtractor={this.optionKeyExtractor}
                  onSelect={this.addOption}
                  selectedOptions={this.state.selectedOptions.options}
                  showValueBubble={false}
                  smallThumb
                  noIcon
                />
              ))}
            </ScrollView>
          </View>
          <View style={styles.summaryContainer}>
            <Text style={styles.label}>{`${I18n.t('yourRecommendedData')}:`}</Text>
            <View style={styles.dataContainer}>
              <Text style={styles.totalData}>{formatValue(this.computeUsage(), 'VOLUME', false, true)}</Text>
            </View>
          </View>
        </GradientPage>
      </FullScreenModal>
    );
  }
}

const getStyles = () =>
  StyleSheet.create({
    container: {
      flex: 1,
      paddingHorizontal: 10,
      paddingTop: 20,
    },
    scrollViewContent: {
      paddingBottom: 20,
    },
    title: {
      marginBottom: 10,
    },
    summaryContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 13,
      paddingHorizontal: 20,
      backgroundColor: theme.bottomPanelColor,
      borderTopWidth: 1,
      borderTopColor: theme.bottomPanelBorderColor,
    },
    label: {
      color: theme.signUpDarkTextColor,
      fontSize: 16,
    },
    dataContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    totalData: {
      fontSize: 24,
      color: theme.signUpTextColor,
    },
  });

type Props = {
  visible: boolean,
  onRequestClose: () => void,
};

type State = {
  selectedOptions: Object,
};
